/**
 * Geofence Drawing Control for Mapbox GL
 * Allows users to draw polygon geofences on the map
 */

import { useControl } from 'react-map-gl';
// @ts-ignore - Mapbox Draw types may not be fully compatible
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import type { ControlPosition } from 'react-map-gl';

interface GeofenceDrawControlProps {
  position?: ControlPosition;
  onCreate?: (geojson: any) => void;
  onUpdate?: (geojson: any) => void;
  onDelete?: (featureIds: string[]) => void;
  displayControlsDefault?: boolean;
  controls?: {
    polygon?: boolean;
    trash?: boolean;
  };
  defaultMode?: string;
}

const GeofenceDrawControl = ({
  position = 'top-left',
  onCreate,
  onUpdate,
  onDelete,
  displayControlsDefault = false,
  controls = {
    polygon: true,
    trash: true
  },
  defaultMode = 'draw_polygon'
}: GeofenceDrawControlProps) => {
  // @ts-ignore - MapboxDraw control type compatibility
  const draw = useControl<any>(
    () =>
      new MapboxDraw({
        displayControlsDefault,
        controls,
        defaultMode,
        styles: [
          // Styling for polygons
          {
            id: 'gl-draw-polygon-fill-inactive',
            type: 'fill',
            filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
            paint: {
              'fill-color': '#ff0000',
              'fill-outline-color': '#ff0000',
              'fill-opacity': 0.1
            }
          },
          {
            id: 'gl-draw-polygon-fill-active',
            type: 'fill',
            filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            paint: {
              'fill-color': '#ff0000',
              'fill-outline-color': '#ff0000',
              'fill-opacity': 0.2
            }
          },
          {
            id: 'gl-draw-polygon-stroke-inactive',
            type: 'line',
            filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            },
            paint: {
              'line-color': '#ff0000',
              'line-width': 2
            }
          },
          {
            id: 'gl-draw-polygon-stroke-active',
            type: 'line',
            filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round'
            },
            paint: {
              'line-color': '#ff0000',
              'line-width': 2
            }
          },
          // Vertex points
          {
            id: 'gl-draw-polygon-and-line-vertex-halo-active',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
            paint: {
              'circle-radius': 7,
              'circle-color': '#FFF'
            }
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-active',
            type: 'circle',
            filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
            paint: {
              'circle-radius': 5,
              'circle-color': '#ff0000'
            }
          },
          // Midpoints
          {
            id: 'gl-draw-polygon-midpoint',
            type: 'circle',
            filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
            paint: {
              'circle-radius': 4,
              'circle-color': '#ff0000'
            }
          }
        ]
      }),
    ({ map }) => {
      if (onCreate) {
        map.on('draw.create', (e: any) => onCreate(e.features[0]));
      }
      if (onUpdate) {
        map.on('draw.update', (e: any) => onUpdate(e.features[0]));
      }
      if (onDelete) {
        map.on('draw.delete', (e: any) => onDelete(e.features.map((f: any) => f.id)));
      }
    },
    ({ map }) => {
      if (onCreate) map.off('draw.create', onCreate);
      if (onUpdate) map.off('draw.update', onUpdate);
      if (onDelete) map.off('draw.delete', onDelete);
    },
    {
      position
    }
  );

  return null;
};

export default GeofenceDrawControl;

